import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { get } from 'lodash-es';
import { Col, Button, FormGroup } from 'reactstrap';

/* Components */
import { FormField } from 'site-modules/shared/components/form-field/form-field';
import { FormFieldMask } from 'site-modules/shared/components/form-field-mask/form-field-mask';
import { PrivacyNote } from 'site-modules/shared/components/lead-form/unified-lead-form/components/privacy-note/privacy-note';
import { HEADLINE_TOOLTIP } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/digital-retail-offer-types';
import { VdpPopoverItem } from 'client/site-modules/shared/components/inventory/vdp-popover-item/vdp-popover-item';
import { ErrorFieldCopy } from 'site-modules/shared/components/lead-form/error-field-copy/error-field-copy';

/* Utils */
import { getUserInfoFromProfile } from 'site-modules/shared/components/lead-form/utils/utils';

/* Constants */
import { PHONE_MASK_V2 } from 'site-modules/shared/constants/lead-form/validation';
import {
  DISCLAIMER_TYPES,
  withDynamicDisclaimer,
} from 'site-modules/shared/components/lead-form/utils/with-dynamic-disclaimer';

import './user-info-price-checker.scss';

const groupClassName = 'mb-0';
const fieldClassName = 'w-100 mb-0';
const fieldErrorClassName = 'medium text-danger';
const HEADLINE_CONFIG = {
  new: {
    header: "Most dealers can't post their lowest prices online.",
    subheader: "Use this form to get the dealer's lowest out-the-door price so you can rate it.",
    tooltip: HEADLINE_TOOLTIP.text,
  },
  used: {
    header: 'Use this form to get the dealer’s price quote.',
  },
};
const HEADLINE = (isUsed, headerText, subHeaderText, hidePricingTooltip) => {
  const { tooltip, header: defaultHeader, subheader: defaultSubheader } = HEADLINE_CONFIG[isUsed ? 'used' : 'new'];
  const header = headerText || defaultHeader;
  const subheader = subHeaderText || defaultSubheader;

  return (
    <Fragment>
      <div className={classNames('heading-4', { 'mb-0_75': !!subheader, 'mb-1_5': !subheader })}>
        <h3 className="d-inline heading-4">{header}</h3>
        {tooltip && !hidePricingTooltip && (
          <VdpPopoverItem placement="bottom" screenReaderTooltipLabel="dealer pricing">
            {tooltip}
          </VdpPopoverItem>
        )}
      </div>
      {subheader && <div className="mb-0_75 text-gray-darker">{subheader}</div>}
    </Fragment>
  );
};

const FORM_LABELS_LEAD_FORM = {
  nameLabel: 'First Name',
  lastNameLabel: 'Last Name',
  emailLabel: 'Email',
  phoneNumLabel: 'Phone',
};

export function UserInfoPriceCheckerUI({
  showSubmitLoadingIndicator = false,
  fieldRef,
  validationErrors = {},
  validate,
  applyPhoneMask = false,
  submitButtonText = 'Submit',
  submitButtonColor = 'primary-b',
  submitButtonSize = 'md',
  profile = {},
  disabled = false,
  tcpaParentDealershipName = null,
  isMobile = false,
  isUsed = false,
  headerText,
  subHeaderText = null,
  hidePricingTooltip = false,
  resetFieldValidation = null,
  disclaimerType = DISCLAIMER_TYPES.BASIC,
}) {
  const PhoneField = applyPhoneMask ? FormFieldMask : FormField;
  const phoneMask = applyPhoneMask ? PHONE_MASK_V2 : null;
  const firstNameValid = !('firstName' in validationErrors);
  const lastNameValid = !('lastName' in validationErrors);
  const emailValid = !('email' in validationErrors);
  const phoneValid = !('phone' in validationErrors);
  const selectedLeadsValid = !('selectedLeadsEmpty' in validationErrors);

  const { firstName, lastName, email, phoneNumber } = getUserInfoFromProfile(profile);
  const headline = HEADLINE(isUsed, headerText, subHeaderText, hidePricingTooltip);

  const submitButton = (
    <div
      className={classNames('submit-button-wrapper d-flex w-100 ', {
        'justify-content-center justify-content-md-start': !isUsed,
        'mb-1_5 mt-0_75': disclaimerType === DISCLAIMER_TYPES.BASIC,
        'mb-0_5': disclaimerType === DISCLAIMER_TYPES.SPECIFIC,
      })}
    >
      <Button
        type="submit"
        color={submitButtonColor}
        size={submitButtonSize}
        className={classNames('submit-button px-1 py-0_5 fw-medium', {
          'spinner-active': showSubmitLoadingIndicator,
          'w-100': isMobile,
        })}
        disabled={disabled || showSubmitLoadingIndicator}
        data-hidedelightedoninteraction="true"
        data-test="submit-personal-info-btn"
      >
        <i className="spinner icon-spinner8 me-0_25" aria-hidden />
        <span>{submitButtonText}</span>
      </Button>
    </div>
  );
  const emailPhoneFormFields = (
    <FormGroup className={classNames(groupClassName, 'form-group')} row noMargin>
      <Col xs={6}>
        <FormField
          id="lead-form-price-checker-email"
          name="email"
          label={FORM_LABELS_LEAD_FORM.emailLabel}
          ref={fieldRef}
          aria-required
          aria-invalid={!emailValid}
          aria-describedby="error-price-checker-email"
          isValid={emailValid}
          type="email"
          className={fieldClassName}
          onBlur={validate}
          onChange={resetFieldValidation}
          defaultValue={email}
          resetTouchOnChange
          autoComplete="email"
          inputClassName="size-16 rounded-8 pt-1_5 pe-0 pb-0_5 ps-1"
          labelClassName="pos-a top-0 left-0 mt-0_5 ms-1 small"
        />
        <ErrorFieldCopy id="error-price-checker-email" className={fieldErrorClassName} copy={validationErrors.email} />
      </Col>

      <Col xs={6}>
        <PhoneField
          id="lead-form-price-checker-phone"
          name="phone"
          label={FORM_LABELS_LEAD_FORM.phoneNumLabel}
          ref={fieldRef}
          aria-required
          aria-invalid={!phoneValid}
          aria-describedby="error-price-checker-phone"
          isValid={phoneValid}
          type="tel"
          mask={phoneMask}
          className={fieldClassName}
          onBlur={validate}
          onChange={resetFieldValidation}
          defaultValue={phoneNumber}
          resetTouchOnChange
          autoComplete="tel"
          inputClassName="size-16 rounded-8 pt-1_5 pe-0 pb-0_5 ps-1"
          labelClassName="pos-a top-0 left-0 mt-0_5 ms-1 small"
        />
        <ErrorFieldCopy id="error-price-checker-phone" className={fieldErrorClassName} copy={validationErrors.phone} />
      </Col>
    </FormGroup>
  );

  return (
    <div className="user-info-price-checker pt-md-0">
      {headline}
      <fieldset>
        <legend className="visually-hidden">Your Contact Information</legend>
        <FormGroup className={classNames(groupClassName, 'form-group')} noMargin row>
          <Col xs={6}>
            <FormField
              id="lead-form-price-checker-fname"
              name="firstName"
              label={FORM_LABELS_LEAD_FORM.nameLabel}
              ref={fieldRef}
              aria-required
              aria-invalid={!firstNameValid}
              aria-describedby="error-price-checker-firstName"
              isValid={firstNameValid}
              className={fieldClassName}
              maxLength={60}
              onBlur={validate}
              onChange={resetFieldValidation}
              defaultValue={firstName}
              resetTouchOnChange
              autoComplete="given-name"
              inputClassName="size-16 rounded-8 pt-1_5 pe-0 pb-0_5 ps-1"
              labelClassName="pos-a top-0 left-0 mt-0_5 ms-1 small"
            />
            <ErrorFieldCopy
              id="error-price-checker-firstName"
              className={fieldErrorClassName}
              copy={validationErrors.firstName}
            />
          </Col>

          <Col xs={6}>
            <FormField
              id="lead-form-price-checker-lname"
              name="lastName"
              label={FORM_LABELS_LEAD_FORM.lastNameLabel}
              ref={fieldRef}
              aria-required
              aria-invalid={!lastNameValid}
              aria-describedby="error-price-checker-lastName"
              isValid={lastNameValid}
              className={fieldClassName}
              maxLength={60}
              onBlur={validate}
              onChange={resetFieldValidation}
              defaultValue={lastName}
              resetTouchOnChange
              autoComplete="family-name"
              inputClassName="size-16 rounded-8 pt-1_5 pe-0 pb-0_5 ps-1"
              labelClassName="pos-a top-0 left-0 mt-0_5 ms-1 small"
            />
            <ErrorFieldCopy
              id="error-price-checker-lastName"
              className={fieldErrorClassName}
              copy={validationErrors.lastName}
            />
          </Col>
        </FormGroup>

        {emailPhoneFormFields}
        {!selectedLeadsValid && <div className="text-danger medium">{validationErrors.selectedLeadsEmpty}</div>}
        {disclaimerType === DISCLAIMER_TYPES.SPECIFIC && (
          <PrivacyNote
            tcpaParentDealershipName={tcpaParentDealershipName}
            classNames="mb-1_5 mt-0_75"
            ctaText={submitButtonText}
            isSingleSelect
            disclaimerType={disclaimerType}
          />
        )}
        {submitButton}
        {disclaimerType === DISCLAIMER_TYPES.BASIC && (
          <PrivacyNote
            tcpaParentDealershipName={tcpaParentDealershipName}
            classNames="mb-0_5"
            ctaText={submitButtonText}
            isSingleSelect
            disclaimerType={disclaimerType}
          />
        )}
      </fieldset>
    </div>
  );
}

UserInfoPriceCheckerUI.propTypes = {
  fieldRef: PropTypes.func.isRequired,
  validate: PropTypes.func.isRequired,
  validationErrors: PropTypes.shape({}),
  submitButtonText: PropTypes.string,
  submitButtonColor: PropTypes.oneOf(['success', 'primary-b', 'blue-50']),
  submitButtonSize: PropTypes.oneOf(['md', 'lg']),
  applyPhoneMask: PropTypes.bool,
  showSubmitLoadingIndicator: PropTypes.bool,
  profile: PropTypes.shape({}),
  disabled: PropTypes.bool,
  tcpaParentDealershipName: PropTypes.string,
  isMobile: PropTypes.bool,
  isUsed: PropTypes.bool,
  headerText: PropTypes.string,
  subHeaderText: PropTypes.string,
  hidePricingTooltip: PropTypes.bool,
  resetFieldValidation: PropTypes.func,
  disclaimerType: PropTypes.oneOf([DISCLAIMER_TYPES.BASIC, DISCLAIMER_TYPES.SPECIFIC]),
};

export const mapStateToProps = state => ({
  profile: get(state, 'profile.data.idm'),
  isMobile: get(state, 'mobile'),
});

export const UserInfoPriceChecker = connect(mapStateToProps)(withDynamicDisclaimer(UserInfoPriceCheckerUI));
